import React, { useCallback, useEffect, useMemo, useState } from "react";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import { icons } from "../../../constants";
import {
  callActionApiAsync,
  callApiAsync,
  getBiomedicalEquipmentModelInspectionProtocol,
  getProtocolActivitySelect,
  postDeleteBiomedicalEquipmentModelInspectionProtocol
} from "../../../services/apiService";
import ModelInspectionProtocolModal from "./ModelInspectionProtocolModal";

const inspectionProtocolColumns = [
  { field: "activityName", header: "Actividad" },
  {
    field: "actionButtons",
    header: "",
    columnType: "actionButtons"
  }
];

const ModelInspectionProtocolTab = ({
  modelRow,
  userId,
  isCatalogAdmin,
  pushToast
}) => {
  const [activityRows, setActivityRows] = useState([]);
  const [activityOptions, setActivityOptions] = useState([]);
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [selectedActivityRow, setSelectedActivityRow] = useState(null);

  const selectedModelId = modelRow?.value ? Number(modelRow.value) : null;

  const loadActivityOptions = useCallback(() => {
    callApiAsync(
      () => getProtocolActivitySelect(),
      (data) => {
        setActivityOptions(data?.protocolActivities || []);
      }
    );
  }, []);

  const loadModelInspectionProtocol = useCallback(() => {
    if (!selectedModelId) {
      setActivityRows([]);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelInspectionProtocol(selectedModelId, userId),
      (data) => {
        setActivityRows(data?.table || []);
      }
    );
  }, [selectedModelId, userId]);

  useEffect(() => {
    if (!selectedModelId) {
      setActivityRows([]);
      setActivityOptions([]);
      return;
    }

    loadActivityOptions();
    loadModelInspectionProtocol();
  }, [loadActivityOptions, loadModelInspectionProtocol, selectedModelId]);

  const handleDeleteActivity = useCallback(
    async (row) => {
      if (!row?.id || !userId) return;
      if (
        !window.confirm(
          `¿Eliminar la actividad "${row.activityName}" del protocolo del modelo seleccionado?`
        )
      ) {
        return;
      }

      await callActionApiAsync(
        () =>
          postDeleteBiomedicalEquipmentModelInspectionProtocol({
            id: row.id,
            userId
          }),
        () => {
          loadModelInspectionProtocol();
          pushToast(
            "success",
            "Protocolo por modelo",
            "La actividad se eliminó de la plantilla del modelo."
          );
        },
        (error) => {
          pushToast(
            "error",
            "Protocolo por modelo",
            typeof error === "string" && error
              ? error
              : "No se pudo eliminar la actividad del protocolo del modelo."
          );
        }
      );
    },
    [loadModelInspectionProtocol, pushToast, userId]
  );

  const tableButtons = useMemo(
    () =>
      isCatalogAdmin
        ? [
            {
              type: "icon",
              icon: icons.editYellow,
              onClick: (row) => {
                setSelectedActivityRow(row);
                setOpenActivityModal(true);
              }
            },
            {
              type: "icon",
              icon: icons.redTrash,
              onClick: handleDeleteActivity
            }
          ]
        : [],
    [handleDeleteActivity, isCatalogAdmin]
  );

  if (!selectedModelId) {
    return (
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-value admin-catalog-hint">
          Seleccione una marca y un modelo para administrar las actividades del
          protocolo de inspección.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-label">Modelo seleccionado</p>
        <p className="admin-catalog-detail-value">{modelRow?.name || "—"}</p>
        <p className="admin-catalog-hint">
          Las actividades configuradas aquí se agregarán automáticamente al
          protocolo de inspección del equipo cuando falten. Los equipos con acta
          enviada a aprobación o aprobada no recibirán ajustes automáticos. Si
          el acta es rechazada y vuelve a edición, podrá cargar las actividades
          nuevas que se hayan agregado al modelo.
        </p>
      </div>

      <div className="mt-25">
        <div className="IHContainer j-space-between mb-15">
          <p className="createRist-title ml-0">
            Actividades del protocolo por modelo
          </p>
          {isCatalogAdmin && (
            <LaddaButton
              width={"170px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              margin={"0"}
              onClick={() => {
                setSelectedActivityRow(null);
                setOpenActivityModal(true);
              }}
            >
              <span className="HContainer align-center">
                <img className="btn-icon mr-10" src={icons.plusIcon} alt="icon" />
                Nueva actividad
              </span>
            </LaddaButton>
          )}
        </div>

        <SimpleTable
          data={activityRows}
          columns={inspectionProtocolColumns}
          scrollHeight={"280px"}
          text={"Actividades del protocolo del modelo"}
          buttons={tableButtons}
          emptyMessage="No hay actividades configuradas para este modelo."
        />
      </div>

      <ModelInspectionProtocolModal
        open={openActivityModal}
        close={() => {
          setOpenActivityModal(false);
          setSelectedActivityRow(null);
        }}
        modelId={selectedModelId}
        userId={userId}
        activityOptions={activityOptions}
        reloadActivityOptions={loadActivityOptions}
        selectedRow={selectedActivityRow}
        onSaved={() => {
          loadActivityOptions();
          loadModelInspectionProtocol();
          pushToast(
            "success",
            "Protocolo por modelo",
            selectedActivityRow
              ? "La actividad del protocolo del modelo se actualizó correctamente."
              : "La actividad se agregó correctamente a la plantilla del modelo."
          );
        }}
        isViewer={!isCatalogAdmin}
      />
    </>
  );
};

export default ModelInspectionProtocolTab;
